import React, { useState, useEffect } from 'react';
import { useAdminBroadcastRecipients } from '../../hooks/useAdmin';
import { SystemAlert, BroadcastRecipient } from '../../types';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  Users, 
  CheckCircle2, 
  Clock, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Mail, 
  Eye, 
  Megaphone 
} from 'lucide-react';

interface BroadcastRecipientsModalProps {
  broadcast: SystemAlert;
  onClose: () => void;
}

export const BroadcastRecipientsModal: React.FC<BroadcastRecipientsModalProps> = ({
  broadcast,
  onClose
}) => {
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<'ALL' | 'SEEN' | 'UNREAD'>('ALL');
  const pageSize = 10;

  const { data: recipientData, isLoading } = useAdminBroadcastRecipients(
    broadcast.id,
    page,
    pageSize
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const rawRecipients: BroadcastRecipient[] = recipientData?.content || [];
  const totalElements = recipientData?.totalElements ?? broadcast.recipientCount;
  const totalPages = recipientData?.totalPages || Math.max(1, Math.ceil(totalElements / pageSize));

  // Filter recipients locally if user selects SEEN or UNREAD
  const displayedRecipients = rawRecipients.filter((r) => {
    if (filter === 'SEEN') return r.read;
    if (filter === 'UNREAD') return !r.read;
    return true;
  });

  const seenCountOnPage = rawRecipients.filter(r => r.read).length;
  const unreadCountOnPage = rawRecipients.filter(r => !r.read).length;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-black border-4 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b-4 border-black dark:border-white p-5 bg-white dark:bg-black flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="blue" className="font-mono text-[10px] uppercase tracking-wider">
                {broadcast.type || 'ANNOUNCEMENT'}
              </Badge>
              <span className="text-xs font-mono font-bold text-[#0044FF] dark:text-blue-400">
                {broadcast.nodeName || (['platform-root', 'root', 'global', 'all'].includes((broadcast.nodeId || '').toLowerCase()) ? 'OrgSets Platform' : `Org: ${broadcast.nodeId}`)}
              </span>
              <span className="text-[11px] font-mono text-gray-500">
                Dispatched {new Date(broadcast.createdAt).toLocaleString()}
              </span>
            </div>
            <h2 className="font-display font-black text-xl sm:text-2xl uppercase tracking-tight text-gray-900 dark:text-white">
              {broadcast.title}
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 max-w-2xl">
              {broadcast.message}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 border-2 border-black dark:border-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors rounded-none cursor-pointer shrink-0"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Top Summary Metric Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 border-b-2 border-black dark:border-white bg-[#F5F5F0] dark:bg-slate-900 divide-x-2 divide-y sm:divide-y-0 divide-black/20 dark:divide-white/20">
          <div className="p-3 sm:p-4 text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Total Audience
            </div>
            <div className="font-display font-black text-xl sm:text-2xl text-gray-900 dark:text-white mt-0.5">
              {totalElements}
            </div>
          </div>

          <div className="p-3 sm:p-4 text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
              Seen on Page
            </div>
            <div className="font-display font-black text-xl sm:text-2xl text-emerald-600 dark:text-emerald-400 mt-0.5 flex items-center justify-center gap-1">
              <CheckCircle2 size={16} />
              <span>{seenCountOnPage}</span>
            </div>
          </div>

          <div className="p-3 sm:p-4 text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 dark:text-amber-400">
              Unopened on Page
            </div>
            <div className="font-display font-black text-xl sm:text-2xl text-amber-600 dark:text-amber-400 mt-0.5 flex items-center justify-center gap-1">
              <Clock size={16} />
              <span>{unreadCountOnPage}</span>
            </div>
          </div>

          <div className="p-3 sm:p-4 text-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 dark:text-blue-400">
              Page Engagement
            </div>
            <div className="font-display font-black text-xl sm:text-2xl text-[#0044FF] dark:text-blue-400 mt-0.5">
              {rawRecipients.length > 0 
                ? Math.round((seenCountOnPage / rawRecipients.length) * 100) 
                : 0}%
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="px-5 py-3 border-b-2 border-black/10 dark:border-white/10 flex items-center justify-between gap-3 bg-white dark:bg-black">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-bold uppercase text-gray-500">Filter:</span>
            <div className="flex gap-1">
              {(['ALL', 'SEEN', 'UNREAD'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setFilter(tab)}
                  className={`text-[11px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 border border-black dark:border-white transition-colors cursor-pointer rounded-none ${
                    filter === tab
                      ? 'bg-black text-white dark:bg-white dark:text-black'
                      : 'bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-900'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <span className="text-[11px] font-mono text-gray-400">
            Showing {displayedRecipients.length} of {rawRecipients.length} recipients
          </span>
        </div>

        {/* Recipient Table Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner size="lg" />
              <span className="font-mono text-xs uppercase tracking-wider text-gray-500">
                Fetching broadcast receipts...
              </span>
            </div>
          ) : displayedRecipients.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-gray-300 dark:border-gray-800 bg-gray-50/50 dark:bg-slate-900/50">
              <Users size={32} className="mx-auto mb-2 opacity-40 text-gray-400" />
              <p className="font-display font-bold uppercase tracking-tight text-sm text-gray-800 dark:text-gray-200">
                No recipients match filter
              </p>
              <p className="text-xs text-gray-500 font-mono mt-1">
                Try switching the filter back to ALL.
              </p>
            </div>
          ) : (
            <div className="border-2 border-black dark:border-white divide-y-2 divide-black dark:divide-white bg-white dark:bg-slate-950">
              {displayedRecipients.map((recipient) => {
                const initials = recipient.name
                  ? recipient.name
                      .split(' ')
                      .map((p) => p[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()
                  : 'U';

                return (
                  <div
                    key={recipient.userId}
                    className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-slate-900/60 transition-colors"
                  >
                    {/* User info */}
                    <div className="flex items-center gap-3 min-w-0">
                      {recipient.imageUrl ? (
                        <img
                          src={recipient.imageUrl}
                          alt={recipient.name}
                          className="w-10 h-10 border-2 border-black dark:border-white object-cover rounded-none shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 border-2 border-black dark:border-white bg-[#FFD600] text-black font-display font-black text-sm flex items-center justify-center rounded-none shrink-0">
                          {initials}
                        </div>
                      )}

                      <div className="min-w-0">
                        <div className="font-display font-black text-sm text-gray-900 dark:text-white truncate">
                          {recipient.name || recipient.email || 'Organization Member'}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                          {recipient.email ? (
                            <>
                              <Mail size={12} className="shrink-0 text-gray-400" />
                              <span className="truncate">{recipient.email}</span>
                            </>
                          ) : (
                            <span className="text-gray-400">Direct Member</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status & Timestamp */}
                    <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0 font-mono text-xs">
                      {recipient.read ? (
                        <div className="text-right">
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-bold uppercase tracking-wider text-[10px]">
                            <CheckCircle2 size={12} />
                            <span>Seen</span>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-1">
                            {recipient.readAt 
                              ? new Date(recipient.readAt).toLocaleString() 
                              : 'Opened (time unrecorded)'}
                          </div>
                        </div>
                      ) : (
                        <div className="text-right">
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-600 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wider text-[10px]">
                            <Clock size={12} />
                            <span>Unopened</span>
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Awaiting user open
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with pagination */}
        <div className="border-t-4 border-black dark:border-white p-4 bg-white dark:bg-black flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs font-mono text-gray-600 dark:text-gray-400">
            Page {page + 1} of {totalPages} &bull; Total {totalElements} recipients
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page === 0 || isLoading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-none border-2 border-black dark:border-white text-xs font-bold uppercase px-3 py-1.5 flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= totalPages - 1 || isLoading}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-none border-2 border-black dark:border-white text-xs font-bold uppercase px-3 py-1.5 flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={onClose}
              className="rounded-none bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider text-xs px-4 py-1.5 ml-2"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
